import React, { useEffect, useState } from "react";
import "../styles/PaymentModal.css";
import { FaCreditCard, FaBitcoin, FaMobileAlt, FaCopy, FaUniversity, FaArrowLeft } from 'react-icons/fa';
import { SiQiwi, SiWebmoney, SiPayeer, SiPerfectmoney } from 'react-icons/si';
import { SidebarBalanceBlock } from "./Sidebar";
import { useAuth } from "../context/AuthContext";
import { usePayment } from "../context/PaymentContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();

const cryptoCurrencies = [
    { id: 'BTC', name: 'Bitcoin', icon: <img src="/assets/crypto/btc.svg" alt="BTC" width="24" height="24" /> },
    { id: 'USDTTRC20', name: 'USDT', icon: <img src="/assets/crypto/tether.svg" alt="USDT" width="24" height="24" /> },
    { id: 'USDC', name: 'USDC', icon: <img src="/assets/crypto/usdc.svg" alt="USDC" width="24" height="24" /> },
    { id: 'ETH', name: 'ETH', icon: <img src="/assets/crypto/eth.svg" alt="ETH" width="24" height="24" /> },
    { id: 'TRX', name: 'Tron', icon: <img src="/assets/crypto/tron.svg" alt="TRX" width="24" height="24" /> },
    { id: 'DOGE', name: 'Doge', icon: <img src="/assets/crypto/doge.svg" alt="DOGE" width="24" height="24" /> },
    { id: 'BCH', name: 'Bitcoin Cash', icon: <img src="/assets/crypto/bch.svg" alt="BCH" width="24" height="24" /> },
    { id: 'XLM', name: 'Stellar', icon: <img src="/assets/crypto/stellar.svg" alt="XLM" width="24" height="24" /> },
    { id: 'XRP', name: 'Xrp', icon: <img src="/assets/crypto/xrp.svg" alt="XRP" width="24" height="24" /> }
];

// Лимиты для депозитов и выводов
const paymentLimits = {
    deposit: {
        RUB: {
            card: { min: 2000, max: 300000, quick: [2000, 5000, 10000] },
            sbp: { min: 5000, max: 300000, quick: [5000, 10000, 20000] },
            'qr-nspk': { min: 100, max: 300000, quick: [1000, 3000, 5000] },
            sberpay: { min: 1000, max: 300000, quick: [1000, 3000, 5000] },
            tpay: { min: 1000, max: 300000, quick: [1000, 3000, 5000] },
            crypto: { min: 100, max: 300000, quick: [2000, 5000, 10000] },
        },
        USD: {
            crypto: { min: 20, max: 3000, quick: [20, 50, 100] }
        },
        BRL: {
            crypto: { min: 100, max: 15000, quick: [100, 250, 500] }
        },
        USDT: {
            crypto: { min: 20, max: 3000, quick: [20, 50, 100] }
        },
        EUR: {
            crypto: { min: 20, max: 2500, quick: [20, 50, 100] }
        }
    },
    withdraw: {
        RUB: {
            card: { min: 3000, max: null, quick: [3000, 5000, 10000] },
            sbp: { min: 3000, max: null, quick: [3000, 5000, 10000] },
            crypto: { min: 3000, max: null, quick: [3000, 5000, 10000] },
        },
        USD: {
            crypto: { min: 30, max: null, quick: [30, 50, 100] }
        },
        BRL: {
            crypto: { min: 150, max: null, quick: [150, 250, 500] }
        },
        USDT: {
            crypto: { min: 30, max: null, quick: [30, 50, 100] }
        },
        EUR: {
            crypto: { min: 25, max: null, quick: [25, 50, 100] }
        }
    }
};

// Функция для получения лимитов по методу, валюте и типу операции
function getLimits(currency, method, transactionType = 'deposit') {
    const operationLimits = paymentLimits[transactionType];
    if (!operationLimits) {
        // Fallback для депозитов
        return { min: 100, max: 300000, quick: [100, 500, 1000] };
    }

    const currencyLimits = operationLimits[currency];
    if (!currencyLimits) {
        // Fallback для неподдерживаемых валют
        return { min: 100, max: transactionType === 'withdraw' ? null : 300000, quick: [100, 500, 1000] };
    }

    return currencyLimits[method] || currencyLimits.crypto || { min: 100, max: transactionType === 'withdraw' ? null : 300000, quick: [100, 500, 1000] };
}

// Функция для получения символа валюты
function getCurrencySymbol(currency) {
    const symbols = {
        'USD': '$',
        'RUB': '₽',
        'BRL': 'R$',
        'USDT': '₮',
        'EUR': '€'
    };
    return symbols[currency] || currency;
}

// Функция для форматирования суммы
// Для криптовалют показывает до 8 знаков после запятой, для фиата - 2 знака
function formatAmount(amount, isCrypto = false) {
    if (!amount) return isCrypto ? '0' : '0.00';
    const num = parseFloat(amount);
    if (isNaN(num)) return isCrypto ? '0' : '0.00';
    
    if (isCrypto) {
        // Для криптовалют: убираем лишние нули в конце, показываем до 8 знаков после запятой
        return parseFloat(num.toFixed(8)).toString();
    }
    
    return num.toFixed(2);
}

const PaymentModal = ({ extId, transactionType, selectedCurrency, onClose, withdrawType, minAmount, maxAmount, defaultAmount, hideBalanceBlock }) => {
    const initialLimits = getLimits(selectedCurrency, 'card', transactionType);
    const [limits, setLimits] = useState({
        min: typeof minAmount === 'number' ? minAmount : initialLimits.min,
        max: typeof maxAmount === 'number' ? maxAmount : initialLimits.max,
        quick: initialLimits.quick
    });
    const [amount, setAmount] = useState(extId ? null : '');
    const formatToTwoDecimals = (val) => {
        if (val === null || val === undefined || val === '') return '';
        const num = Number(val);
        if (Number.isNaN(num)) return '';
        // Округляем в меньшую сторону до 2 знаков после запятой
        return (Math.floor(num * 100) / 100).toFixed(2);
    };
    const [cardNumber, setCardNumber] = useState("");
    const [phone, setPhone] = useState("");
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(extId ? "loading" : "select_method");
    const [timeLeft, setTimeLeft] = useState(15 * 60);
    const [error, setError] = useState("");
    const [paymentExtId, setPaymentExtId] = useState(extId);
    const [isPaid, setIsPaid] = useState(false);
    const [selectedCrypto, setSelectedCrypto] = useState(null);
    const [selectedCryptoName, setSelectedCryptoName] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const { showNotification } = useAuth();
    const { verificationData, profile } = usePayment();
    const navigate = useNavigate();
    const [fio, setFio] = useState("");
    const { t } = useTranslation();
    const [wallet, setWallet] = useState("");
    const [bankName, setBankName] = useState("");
    const [availablePromoCodes, setAvailablePromoCodes] = useState([]);
    const [selectedPromoCode, setSelectedPromoCode] = useState(null);
    
    // Проверка для аккаунтов без Telegram - требуется подтвержденный email
    const requiresEmailVerification = !profile?.telegram_id && !profile?.emailVerified;

    // При открытии ввода суммы для реферального вывода — подставляем сумму по умолчанию и форматируем до 2 знаков
    useEffect(() => {
        if (transactionType === 'withdraw' && withdrawType === 'referal' && status === 'enter_amount') {
            if (typeof defaultAmount === 'number' && defaultAmount >= 0) {
                setAmount(formatToTwoDecimals(defaultAmount));
            }
        }
    }, [transactionType, withdrawType, status, defaultAmount]);

    useEffect(() => {
        if (paymentMethod) {
            const newLimits = getLimits(selectedCurrency, paymentMethod, transactionType);
            setLimits({
                min: typeof minAmount === 'number' ? minAmount : newLimits.min,
                max: typeof maxAmount === 'number' ? maxAmount : newLimits.max,
                quick: newLimits.quick
            });
            // Не устанавливаем предзаполненную сумму
            setAmount('');
        }
    }, [paymentMethod, selectedCurrency, minAmount, maxAmount, defaultAmount, transactionType]);

    // paymentMethods теперь формируется динамически с учётом типа операции
    let paymentMethods = [];
    if (transactionType === 'withdraw') {
        if (selectedCurrency === 'RUB') {
            paymentMethods = [
                {
                    id: 'card',
                    name: t("PaymentModal.card"),
                    icon: <FaCreditCard className="payment-method-icon" />
                },
                {
                    id: 'sbp',
                    name: t('paymentModal.sbp'),
                    icon: <FaMobileAlt className="payment-method-icon" />
                },
                {
                    id: 'crypto',
                    name: t('paymentModal.crypto'),
                    icon: <FaBitcoin className="payment-method-icon" />
                }
            ];
        } else {
            paymentMethods = [
                {
                    id: 'crypto',
                    name: t('paymentModal.crypto'),
                    icon: <FaBitcoin className="payment-method-icon" />
                }
            ];
        }
    } else {
        // deposit
        paymentMethods = [
            {
                id: 'crypto',
                name: t('paymentModal.crypto'),
                icon: <FaBitcoin className="payment-method-icon" />
            }
        ];
        if (selectedCurrency === 'RUB') {
            paymentMethods.unshift(
                {
                    id: 'qr-nspk',
                    name: t("PaymentModal.qrNspk"),
                    icon: <img src="/assets/methods/qr-nspk.png" alt="QR НСПК" width={32} height={32} />
                },
                {
                    id: 'card',
                    name: t("PaymentModal.card"),
                    icon: <FaCreditCard className="payment-method-icon" />
                },
                {
                    id: 'sbp',
                    name: t('PaymentModal.sbp'),
                    icon: <FaMobileAlt className="payment-method-icon" />
                },
                {
                    id: 'tpay',
                    name: t('PaymentModal.tpay'),
                    icon: <img src="/assets/methods/TPay.png" alt="TPay" width={32} height={32} />
                }
            );
        }
    }

    // Фильтрация криптовалют по поисковому запросу
    const filteredCryptos = cryptoCurrencies.filter(crypto =>
        crypto.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        crypto.id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        if (extId) {
            setStatus("loading");
            fetchPaymentDetails();
        } else {
            setStatus("select_method");
            // Загружаем доступные промокоды для депозита
            if (transactionType !== 'withdraw') {
                fetchAvailablePromoCodes();
            }
        }
    }, [extId, transactionType]);

    // Функция загрузки доступных промокодов
    const fetchAvailablePromoCodes = async () => {
        try {
            const response = await fetch(`${API_URL}/api/promo/user-activated`, {
                method: "GET",
                credentials: "include",
            });
            const data = await response.json();
            if (data.success) {
                const promoCodes = data.activatedPromoCodes || [];
                setAvailablePromoCodes(promoCodes);

                // Сбрасываем выбранный промокод если он стал использованным или недоступным
                if (selectedPromoCode) {
                    const selectedPromo = promoCodes.find(promo => promo.id === selectedPromoCode);
                    if (!selectedPromo || selectedPromo.is_used) {
                        setSelectedPromoCode(null);
                    }
                }

                // Если промокод ещё не выбран, выбираем первый доступный по умолчанию
                if (!selectedPromoCode && promoCodes.length > 0) {
                    setSelectedPromoCode(promoCodes[0].id);
                }
            }
        } catch (error) {
            console.error('Ошибка при загрузке промокодов:', error);
        }
    };

    // Убираем проверку минимальной суммы с фронтенда - теперь это делается в depositController

    // ✅ Функция загрузки деталей платежа
    const fetchPaymentDetails = async () => {
        if (!paymentExtId) return;
        try {
            let url;
            if (transactionType === 'withdraw') {
                url = `${API_URL}/api/withdrawal-by-id?extId=${paymentExtId}`;
            } else {
                url = `${API_URL}/api/payment-status?extId=${paymentExtId}`;
            }
            const response = await fetch(url, {
                method: "GET",
                credentials: "include",
            });
            const data = await response.json();
            console.log('🔍 [fetchPaymentDetails] Получены данные:', {
                extId: data.extId,
                expiresAt: data.expiresAt,
                expiresAtType: typeof data.expiresAt,
                currentTime: new Date().toISOString(),
                amount: data.amount
            });
            if (data.success) {
                setPaymentDetails(data);
                // Для заявок на вывод — статус по status, для платежей — как раньше
                if (transactionType === 'withdraw') {
                    if (data.status === "SUCCESSFUL") setStatus("success");
                    else if (data.status === "DENIED") setStatus("denied");
                    else if (data.status === "WAITING" || data.status === "pending") setStatus("waiting_withdrawal");
                    else setStatus("waiting_withdrawal");
                } else {
                    setStatus(
                        data.status === "SUCCESSFUL"
                            ? "success"
                            : data.status === "DENIED"
                                ? "denied"
                                : "waiting_payment"
                    );
                    if (data.status === "CLIENTPAID") {
                        setIsPaid(true);
                    }
                }
            } else {
                showNotification(data.message || t("paymentModal.errors.fetchStatus"), "error");
            }
        } catch {
            showNotification(t("paymentModal.errors.server"), "error");
        }
    };

    // ✅ Создание нового платежа
    const handlePaymentRequest = async () => {
        if (!amount || isNaN(amount) || parseFloat(amount) < limits.min || (limits.max && parseFloat(amount) > limits.max)) {
            if (parseFloat(amount) < limits.min) {
                showNotification(`Минимальная сумма: ${limits.min} ${getCurrencySymbol(selectedCurrency)}`, "error");
                return;
            }
            if (limits.max && parseFloat(amount) > limits.max) {
                showNotification(`Максимальная сумма: ${limits.max} ${getCurrencySymbol(selectedCurrency)}`, "error");
                return;
            }
            showNotification(t("paymentModal.errors.enterAmount"), "error");
            return;
        }

        setLoading(true);
        setError("");


        try {
            const response = await fetch(`${API_URL}/api/create-payment`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    amount: parseFloat(amount),
                    paymentMethod,
                    currency: selectedCurrency,
                    crypto: selectedCrypto,
                    cryptoName: selectedCryptoName,
                    promoActivationId: selectedPromoCode
                }),
            });

            const data = await response.json();

            console.log('🔍 [handlePaymentRequest] Создан платеж:', {
                extId: data.extId,
                expiresAt: data.expiresAt,
                expiresAtType: typeof data.expiresAt,
                currentTime: new Date().toISOString(),
                amount: data.amount
            });

            if (data.success) {
                setPaymentDetails(data);
                setPaymentExtId(data.extId);
                setStatus("waiting_payment");

            } else {
                showNotification(data.message || t("paymentModal.errors.create"), "error");
            }
        } catch {
            showNotification(t("paymentModal.errors.server"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (status === "waiting_payment" && paymentExtId) {
            fetchPaymentDetails();
            const interval = setInterval(fetchPaymentDetails, 5000);
            return () => clearInterval(interval);
        }
    }, [status, paymentExtId]);

    const handleClose = () => {
        setPaymentDetails(null);
        setPaymentExtId(null);
        setStatus("select_method");
        setError("");
        setIsPaid(false);
        onClose();
    };

    const handleMethodSelect = (method) => {
        setPaymentMethod(method);
        if (method === 'crypto') {
            setStatus("select_crypto");
        } else {
            setStatus("enter_amount");
        }
    };

    const handleCryptoSelect = (crypto) => {
        setSelectedCrypto(crypto.id);
        setSelectedCryptoName(crypto.name);
        setPaymentMethod('crypto');
        setStatus("enter_amount");
    };

    const handleBack = () => {
        if (status === "enter_amount") {
            setStatus("select_method");
            setPaymentMethod(null);
        } else {
            handleClose();
        }
    };

    const handleWithdrawRequest = async () => {
        if (!amount || isNaN(amount) || parseFloat(amount) < limits.min || (limits.max && parseFloat(amount) > limits.max)) {
            if (parseFloat(amount) < limits.min) {
                showNotification(`Минимальная сумма вывода: ${limits.min} ${getCurrencySymbol(selectedCurrency)}`, "error");
                return;
            }
            if (limits.max && parseFloat(amount) > limits.max) {
                showNotification(`Максимальная сумма вывода: ${limits.max} ${getCurrencySymbol(selectedCurrency)}`, "error");
                return;
            }
            showNotification(t("paymentModal.errors.enterAmount"), "error");
            return;
        }
        if (!fio.trim()) {
            showNotification(t("paymentModal.errors.enterFio"), "error");
            return;
        }
        if (paymentMethod === 'card' && !cardNumber.trim()) {
            showNotification(t("paymentModal.errors.enterCard"), "error");
            return;
        }
        if (paymentMethod === 'sbp' && !phone.trim()) {
            showNotification(t("paymentModal.errors.enterPhone"), "error");
            return;
        }
        if (paymentMethod === 'crypto' && !wallet.trim()) {
            showNotification(t("paymentModal.errors.enterWallet"), "error");
            return;
        }
        if (paymentMethod === 'sbp' && !bankName.trim()) {
            showNotification(t("paymentModal.errors.enterBankName"), "error");
            return;
        }

        setLoading(true);
        setError("");
        try {
            // Округляем сумму вниз до 2 знаков при отправке
            const amountNum = parseFloat(amount);
            const roundedAmount = Math.floor(amountNum * 100) / 100;
            const body = {
                amount: roundedAmount,
                fio,
                cardNumber: cardNumber || null,
                phone: phone || null,
                paymentMethod: paymentMethod === 'crypto' ? selectedCrypto : paymentMethod,
                wallet: wallet || null,
                bankName: bankName || null,
                type: withdrawType || 'player',
                currency: selectedCurrency
            };
            const response = await fetch(`${API_URL}/api/request-withdrawal`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await response.json();
            if (data.success) {
                setStatus("waiting_withdrawal");
                showNotification(t("paymentModal.success.withdrawRequestCreated"), "success");
            } else {
                showNotification(data.message || t("paymentModal.errors.withdrawRequestCreate"), "error");
            }
        } catch {
            showNotification(t("paymentModal.errors.server"), "error");
        } finally {
            setLoading(false);
        }
    };

    // Функция копирования в буфер обмена
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showNotification(t("paymentModal.success.copied"), "success");
    };

    // Функция для перехода на страницу профиля
    const handleGoToProfile = () => {
        onClose();
        navigate('/profile');
    };

    // Форматирование времени
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}м : ${remainingSeconds.toString().padStart(2, '0')}с`;
    };

    // Функция для корректной обработки времени истечения
    const parseExpirationTime = (expiresAt) => {
        if (!expiresAt) return null;

        // Добавляем отладочную информацию
        console.log('🔍 [PaymentModal] Parsing expiresAt:', expiresAt, 'Type:', typeof expiresAt);

        let expirationDate;

        // Если это timestamp в миллисекундах
        if (typeof expiresAt === 'number') {
            expirationDate = new Date(expiresAt);
        }
        // Если это timestamp в секундах (Unix timestamp)
        else if (typeof expiresAt === 'string' && /^\d+$/.test(expiresAt)) {
            const timestamp = parseInt(expiresAt);
            // Если меньше чем timestamp 2000 года, то это секунды, иначе миллисекунды
            expirationDate = timestamp < 946684800000 ? new Date(timestamp * 1000) : new Date(timestamp);
        }
        // Если это строка даты
        else if (typeof expiresAt === 'string') {
            // Теперь все даты в UTC, поэтому парсим как есть
            expirationDate = new Date(expiresAt);
        }
        else {
            expirationDate = new Date(expiresAt);
        }

        console.log('🔍 [PaymentModal] Final parsed date:', expirationDate, 'Valid:', !isNaN(expirationDate.getTime()));

        return isNaN(expirationDate.getTime()) ? null : expirationDate;
    };

    // Таймер обратного отсчета до expiresAt
    useEffect(() => {
        if (status === "waiting_payment" && paymentDetails && paymentDetails.expiresAt) {
            console.log('🔍 [Timer useEffect] Начинаем таймер:', {
                expiresAt: paymentDetails.expiresAt,
                expiresAtType: typeof paymentDetails.expiresAt,
                currentTime: new Date().toISOString()
            });

            const updateTimer = () => {
                const expirationDate = parseExpirationTime(paymentDetails.expiresAt);

                if (!expirationDate) {
                    console.warn('⚠️ [Timer] Не удалось распарсить дату, устанавливаем 15 минут по умолчанию');
                    const defaultExpiration = Date.now() + (15 * 60 * 1000);
                    const diff = Math.floor((defaultExpiration - Date.now()) / 1000);
                    setTimeLeft(diff > 0 ? diff : 0);
                    return;
                }

                const expires = expirationDate.getTime();
                const now = Date.now();
                const diff = Math.floor((expires - now) / 1000);

                console.log('⏱️ [Timer] Обновление:', {
                    expiresISO: new Date(expires).toISOString(),
                    nowISO: new Date(now).toISOString(),
                    diffSeconds: diff,
                    diffMinutes: Math.floor(diff / 60)
                });

                setTimeLeft(diff > 0 ? diff : 0);
            };

            updateTimer();
            const timer = setInterval(updateTimer, 1000);

            return () => {
                console.log('🛑 [Timer] Останавливаем таймер');
                clearInterval(timer);
            };
        }
    }, [status, paymentDetails]);

    const updatePaymentStatus = async () => {
        try {
            // Проверяем, можно ли обновить статус
            if (paymentDetails.status === 'SUCCESSFUL' || paymentDetails.status === 'DENIED') {
                showNotification(t("paymentModal.errors.cannotUpdateCompleted"), "error");
                return;
            }

            const response = await fetch(`${API_URL}/api/update-payment-status`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    extId: paymentDetails.extId,
                    status: 'CLIENTPAID'
                })
            });

            if (!response.ok) {
                throw new Error(t("paymentModal.errors.updateStatus"));
            }

            const data = await response.json();
            if (data.success) {
                setIsPaid(true);
                showNotification(t("paymentModal.success.statusUpdated"), "success");
            } else {
                showNotification(data.message || t("paymentModal.errors.updateStatus"), "error");
            }
        } catch (error) {
            console.error(t("paymentModal.errors.updateStatus"), error);
            showNotification(t("paymentModal.errors.updateStatusGeneric"), "error");
        }
    };

    return (
        <div className="payment-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
                handleClose();
            }
        }}>
            <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
                <button className="profile-modal-close" onClick={handleClose}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#23232e" />
                        <path d="M16 8L8 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                        <path d="M8 8L16 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>
                <h3 className="profile-modal-title">{transactionType === 'withdraw' ? t('paymentModal.withdrawTitle') : t('paymentModal.depositTitle')}</h3>

                {status === "loading" && <h2>{t('paymentModal.loading')}</h2>}

                {status === "select_method" && (
                    <>
                        {!hideBalanceBlock && (
                            <div className="CurrencyChange">
                                <SidebarBalanceBlock />
                            </div>
                        )}

                        {/* Проверка верификации для операций вывода */}
                        {transactionType === 'withdraw' && !verificationData?.isVerified && (
                            <div className="verification-required-container">
                                <div className="verification-warning">
                                    <div className="warning-icon">⚠️</div>
                                    <div className="warning-content">
                                        <h4>Аккаунт не верифицирован</h4>
                                        <p>Функциональность ресурса и доступность выплат ограничены.</p>
                                    </div>
                                </div>
                                <button
                                    className="verification-button"
                                    onClick={handleGoToProfile}
                                >
                                    Пройти верификацию
                                </button>
                            </div>
                        )}

                        {/* Проверка верификации email для выводов (только для аккаунтов без Telegram) */}
                        {transactionType === 'withdraw' && verificationData?.isVerified && requiresEmailVerification && (
                            <div className="verification-required-container">
                                <div className="verification-warning">
                                    <div className="warning-icon">⚠️</div>
                                    <div className="warning-content">
                                        <h4>Email не подтвержден</h4>
                                        <p>Для вывода средств необходимо подтвердить email или привязать Telegram аккаунт в профиле.</p>
                                    </div>
                                </div>
                                <button
                                    className="verification-button"
                                    onClick={handleGoToProfile}
                                >
                                    Перейти в профиль
                                </button>
                            </div>
                        )}

                        {/* Проверка верификации email для депозитов (только для аккаунтов без Telegram) */}
                        {transactionType !== 'withdraw' && requiresEmailVerification && (
                            <div className="verification-required-container">
                                <div className="verification-warning">
                                    <div className="warning-icon">⚠️</div>
                                    <div className="warning-content">
                                        <h4>Email не подтвержден</h4>
                                        <p>Для пополнения баланса необходимо подтвердить email или привязать Telegram аккаунт в профиле.</p>
                                    </div>
                                </div>
                                <button
                                    className="verification-button"
                                    onClick={handleGoToProfile}
                                >
                                    Перейти в профиль
                                </button>
                            </div>
                        )}

                        {/* Методы оплаты отображаются только для верифицированных пользователей при выводе и для пользователей с подтвержденным email при депозите */}
                        {(transactionType !== 'withdraw' || (verificationData?.isVerified && !requiresEmailVerification)) && (transactionType === 'withdraw' || !requiresEmailVerification) && (
                            <>
                                {transactionType !== 'withdraw' && (
                                    <div className="payment-method-hint">
                                        {t('paymentModal.minAmountDependsOnMethod', 'Мин. сумма зависит от метода оплаты')}
                                    </div>
                                )}
                                <div className="payment-methods-grid">
                                {paymentMethods.map(method => (
                                    <div
                                        key={method.id}
                                        className="payment-method-card"
                                        onClick={() => handleMethodSelect(method.id)}
                                        style={{ backgroundImage: method.id === 'qr-nspk' ? 'url(/assets/methods/qr-nspk.png)' : method.id === 'card' ? 'url(/assets/methods/master-visa-mir.png)' : method.id === 'sbp' ? 'url(/assets/methods/sbp.png)' : method.id === 'sberpay' ? 'url(/assets/methods/SberPay.svg)' : method.id === 'tpay' ? 'url(/assets/methods/TPay.png)' : method.id === 'crypto' ? 'url(/assets/methods/btc.png)' : undefined }}
                                    >
                                        <div className="method-name">{method.name}</div>
                                    </div>
                                ))}
                                </div>
                            </>
                        )}

                        {/* Блок выбора промокодов для депозита (только для депозитов) */}
                        {transactionType !== 'withdraw' && availablePromoCodes.filter(promo => !promo.is_used).length > 0 && (
                            <div className="promo-codes-section">
                                <h4 className="promo-codes-title">{t('paymentModal.selectPromoCode', 'Выберите промокод')}</h4>
                                <div className="promo-codes-grid">
                                    {availablePromoCodes
                                        .filter(promo => !promo.is_used) // Показываем только неиспользованные промокоды
                                        .map(promo => (
                                            <div
                                                key={promo.id}
                                                className={`promo-code-card ${selectedPromoCode === promo.id ? 'active' : ''}`}
                                                onClick={() => setSelectedPromoCode(selectedPromoCode === promo.id ? null : promo.id)}
                                            >
                                                <div className="promo-card-content">
                                                    <div className="promo-card-icon">
                                                        <img src="/assets/banners/promostar.webp" alt="Stars" className="promo-icon-image" />
                                                    </div>
                                                    <div className="promo-card-info">
                                                        <div className="promo-card-title">{promo.code}</div>
                                                        <div className="promo-card-description">
                                                            {promo.bonus_percent > 0 ? `+${promo.bonus_percent}%` : ''} на {promo.min_deposit > 0 ? `депозит от ${promo.min_deposit} ₽` : 'депозит'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="promo-card-toggle">
                                                    <div className={`toggle-switch ${selectedPromoCode === promo.id ? 'active' : ''}`}>
                                                        <div className="toggle-slider"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {/* Текст соглашения показывается только когда доступны методы оплаты */}
                        {(transactionType !== 'withdraw' || (verificationData?.isVerified && !requiresEmailVerification)) && (transactionType === 'withdraw' || !requiresEmailVerification) && (
                            <div className="payment-agreement-hint">
                                {t('paymentModal.agreementHint')}
                            </div>
                        )}
                    </>
                )}

                {status === "enter_amount" && transactionType === 'withdraw' && (
                    <>
                        <div className="selected-method">
                            <div className="method-info">
                                {paymentMethod === 'card' && (
                                    <>
                                        <div className="method-icon">
                                            <FaCreditCard size={24} color="#4A9FFF" />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t('paymentModal.bankCard')}</div>
                                            <div className="method-name">{t('paymentModal.cardTransferType')}</div>
                                        </div>
                                    </>
                                )}
                                {paymentMethod === 'sbp' && (
                                    <>
                                        <div className="method-icon">
                                            <img src="/assets/methods/sbp-Symbol.svg" alt={t('paymentModal.sbp')} width={32} height={32} />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t('paymentModal.sbpType')}</div>
                                            <div className="method-name">{t('paymentModal.sbpTransferType')}</div>
                                        </div>
                                    </>
                                )}
                                {paymentMethod === 'crypto' && (
                                    <>
                                        <div className="method-icon">
                                            <FaBitcoin size={24} color="#F7931A" />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t('paymentModal.cryptoType')}</div>
                                            <div className="method-name">{t('paymentModal.cryptoWithdrawType')}</div>
                                        </div>
                                    </>
                                )}
                                <button className="change-method" onClick={() => setStatus("select_method")}>{t('paymentModal.changeMethod')}</button>
                            </div>
                        </div>
                        <div className="payment-amount-container">
                            <div className="amount-input-wrapper">
                                <input
                                    type="number"
                                    placeholder={t('paymentModal.amountPlaceholder')}
                                    min={limits.min}
                                    max={limits.max}
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    onBlur={() => setAmount(formatToTwoDecimals(amount))}
                                    disabled={withdrawType === 'referal'}
                                />
                                <div className="amount-limits">
                                    <span className="limit-min">MIN {formatToTwoDecimals(limits.min)} {getCurrencySymbol(selectedCurrency)}</span>
                                    {limits.max && (
                                        <span className="limit-max">MAX {formatToTwoDecimals(limits.max)} {getCurrencySymbol(selectedCurrency)}</span>
                                    )}
                                </div>
                            </div>
                            {withdrawType !== 'referal' && (
                                <div className="quick-amounts">
                                    {limits.quick.map((val) => (
                                        <button key={val} onClick={() => setAmount(val)}>{val} {getCurrencySymbol(selectedCurrency)}</button>
                                    ))}
                                </div>
                            )}

                            <input
                                type="text"
                                placeholder={t('paymentModal.fioPlaceholder')}
                                value={fio}
                                onChange={e => setFio(e.target.value)}
                            />
                            {paymentMethod === 'card' && (
                                <input
                                    type="text"
                                    placeholder={t('paymentModal.cardPlaceholder')}
                                    value={cardNumber}
                                    onChange={e => setCardNumber(e.target.value)}
                                />
                            )}
                            {paymentMethod === 'sbp' && (
                                <input
                                    type="text"
                                    placeholder={t('paymentModal.phonePlaceholder')}
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                />
                            )}
                            {paymentMethod === 'crypto' && (
                                <input
                                    type="text"
                                    placeholder={t('paymentModal.walletPlaceholder')}
                                    value={wallet}
                                    onChange={e => setWallet(e.target.value)}
                                />
                            )}
                            {paymentMethod === 'sbp' && (
                                <input
                                    type="text"
                                    placeholder={t('paymentModal.bankNamePlaceholder') || 'Название банка'}
                                    value={bankName}
                                    onChange={e => setBankName(e.target.value)}
                                />
                            )}
                            <button className="button-payment" onClick={handleWithdrawRequest} disabled={loading}>
                                {loading ? t('paymentModal.loading') : t('paymentModal.withdrawBtn')}
                            </button>
                        </div>
                    </>
                )}

                {status === "enter_amount" && transactionType !== 'withdraw' && (
                    <>
                        <div className="selected-method">
                            <div className="method-info">
                                {paymentMethod === 'card' && (
                                    <>
                                        <div className="method-icon">
                                            <FaCreditCard size={24} color="#4A9FFF" />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t('paymentModal.bankCard')}</div>
                                            <div className="method-name">{t('paymentModal.cardTransferType')}</div>
                                        </div>
                                    </>
                                )}
                                {paymentMethod === 'sbp' && (
                                    <>
                                        <div className="method-icon">
                                            <img src="/assets/methods/sbp-Symbol.svg" alt={t('paymentModal.sbp')} width={32} height={32} />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t('paymentModal.sbpType')}</div>
                                            <div className="method-name">{t('paymentModal.sbpTransferType')}</div>
                                        </div>
                                    </>
                                )}
                                {paymentMethod === 'qr-nspk' && (
                                    <>
                                        <div className="method-icon">
                                            <img src="/assets/methods/nspk.svg" alt={t('paymentModal.sbp')} width={32} height={32} />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t("paymentModal.qrNspk")}</div>
                                            <div className="method-name">{t("paymentModal.qrNspkDesc")}</div>
                                        </div>
                                    </>
                                )}
                                {paymentMethod === 'crossborder' && (
                                    <>
                                        <div className="method-icon">
                                            <FaUniversity size={24} color="#FF9800" />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t('paymentModal.crossborderType')}</div>
                                            <div className="method-name">{t('paymentModal.crossborderHint')}</div>
                                        </div>
                                    </>
                                )}
                                {paymentMethod === 'crypto' && (
                                    <>
                                        <div className="method-icon">
                                            <FaBitcoin size={24} color="#F7931A" />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t('paymentModal.cryptoDepositHint')}</div>
                                            <div className="method-name">{t('paymentModal.cryptoDepositType')}</div>
                                        </div>
                                    </>
                                )}
                                {paymentMethod === 'tpay' && (
                                    <>
                                        <div className="method-icon">
                                            <img src="/assets/methods/TPay.png" alt="TPay" width={32} height={32} />
                                        </div>
                                        <div className="method-details">
                                            <div className="method-type">{t('paymentModal.tpayType')}</div>
                                            <div className="method-name">{t('paymentModal.tpayDepositType')}</div>
                                        </div>
                                    </>
                                )}
                                <button className="change-method" onClick={() => setStatus("select_method")}>{t('paymentModal.changeMethod')}</button>
                            </div>
                        </div>
                        <div className="payment-amount-container">
                            <div className="amount-input-wrapper">
                                <input
                                    type="number"
                                    placeholder={t('paymentModal.amountPlaceholder')}
                                    min={limits.min}
                                    max={limits.max}
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    onBlur={() => setAmount(formatToTwoDecimals(amount))}
                                />
                                <div className="amount-limits">
                                    <span className="limit-min">MIN {formatToTwoDecimals(limits.min)} {getCurrencySymbol(selectedCurrency)}</span>
                                    {limits.max && (
                                        <span className="limit-max">MAX {formatToTwoDecimals(limits.max)} {getCurrencySymbol(selectedCurrency)}</span>
                                    )}
                                </div>
                            </div>
                            <div className="quick-amounts">
                                {limits.quick.map((val) => (
                                    <button key={val} onClick={() => setAmount(val)}>{val} {getCurrencySymbol(selectedCurrency)}</button>
                                ))}
                            </div>

                            <button className="button-payment" onClick={handlePaymentRequest} disabled={loading}>
                                {loading ? t('paymentModal.loading') : t('paymentModal.depositBtn')}
                            </button>
                        </div>
                    </>
                )}

                {status === "select_crypto" && (
                    <>
                        <div className="method-info">
                            <div className="method-icon">
                                <FaBitcoin size={24} color="#F7931A" />
                            </div>
                            <div className="method-details">
                                <div className="method-type">{t('paymentModal.selectCryptoTitle')}</div>
                                <div className="method-name">{t('paymentModal.selectCryptoSubtitle')}</div>
                            </div>
                            <button className="change-method" onClick={() => setStatus("select_method")}>{t('paymentModal.changeMethod')}</button>
                        </div>

                        <div className="crypto-search">
                            <input
                                type="text"
                                placeholder={t('paymentModal.search')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="crypto-grid">
                            {filteredCryptos.map(crypto => (
                                <div
                                    key={crypto.id}
                                    className="crypto-item"
                                    onClick={() => handleCryptoSelect(crypto)}
                                >
                                    <div className="crypto-icon">
                                        {crypto.icon}
                                    </div>
                                    <span>{crypto.name}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {status === "waiting_payment" && paymentDetails && (
                    <>
                        <div className="waiting-stage-wrapper">

                            <div className="waiting_reqs">
                                <div className="payment-detail-row">
                                    <div className="payment-detail-info">
                                        <p>{t('paymentDetailAmount')}</p>
                                        {paymentDetails.requisites ? (
                                            <b>{formatAmount(paymentDetails.totalAmountInUsdt, true)} {paymentDetails.cryptoCurrency === 'USDTTRC20' ? 'USDT' : paymentDetails.cryptoCurrency || 'USDT'}</b>
                                        ) : (
                                            <b>{formatAmount(paymentDetails.amount)} {getCurrencySymbol(selectedCurrency)}</b>
                                        )}
                                    </div>
                                    <button
                                        className="copy-button"
                                        onClick={() => copyToClipboard(paymentDetails.requisites ? formatAmount(paymentDetails.totalAmountInUsdt, true) : formatAmount(paymentDetails.amount))}
                                    >
                                        <FaCopy size={16} />
                                    </button>
                                </div>

                                {paymentDetails.cardNumber && !paymentDetails.provider?.includes('tpay') && (
                                    <>
                                        <div className="payment-detail-row">
                                            <div className="payment-detail-info">
                                                <p>{t('paymentDetailCard')}</p>
                                                <b>{paymentDetails.cardNumber}</b>
                                            </div>
                                            <button
                                                className="copy-button"
                                                onClick={() => copyToClipboard(paymentDetails.cardNumber)}
                                            >
                                                <FaCopy size={16} />
                                            </button>
                                        </div>
                                        {paymentDetails.owner && (
                                            <div className="payment-detail-row">
                                                <div className="payment-detail-info">
                                                    <p>{t('paymentDetailOwner') || 'Владелец карты'}</p>
                                                    <b>{paymentDetails.owner}</b>
                                                </div>
                                            </div>
                                        )}
                                        <div className="payment-detail-row">
                                            <div className="payment-detail-info">
                                                <p>{t('paymentDetailBank')}</p>
                                                <b>{paymentDetails.bankName}</b>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {paymentDetails.phoneNumber && !paymentDetails.provider?.includes('tpay') && (
                                    <>
                                        <div className="payment-detail-row">
                                            <div className="payment-detail-info">
                                                <p>{t('paymentDetailPhone')}</p>
                                                <b>{paymentDetails.phoneNumber}</b>
                                            </div>
                                            <button
                                                className="copy-button"
                                                onClick={() => copyToClipboard(paymentDetails.phoneNumber)}
                                            >
                                                <FaCopy size={16} />
                                            </button>
                                        </div>
                                        {paymentDetails.owner && (
                                            <div className="payment-detail-row">
                                                <div className="payment-detail-info">
                                                    <p>{t('paymentDetailOwner') || 'Владелец'}</p>
                                                    <b>{paymentDetails.owner}</b>
                                                </div>
                                            </div>
                                        )}
                                        <div className="payment-detail-row">
                                            <div className="payment-detail-info">
                                                <p>{t('paymentDetailBank')}</p>
                                                <b>{paymentDetails.bankName}</b>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Реквизиты для TPay */}
                                {(paymentDetails.provider === 'tpay' || paymentDetails.provider?.includes('tpay')) && (paymentDetails.cardNumber || paymentDetails.phoneNumber) && (
                                    <>
                                        {paymentDetails.cardNumber && (
                                            <div className="payment-detail-row">
                                                <div className="payment-detail-info">
                                                    <p>{t('paymentDetailCard') || 'Номер карты'}</p>
                                                    <b>{paymentDetails.cardNumber}</b>
                                                </div>
                                                <button
                                                    className="copy-button"
                                                    onClick={() => copyToClipboard(paymentDetails.cardNumber)}
                                                >
                                                    <FaCopy size={16} />
                                                </button>
                                            </div>
                                        )}
                                        {paymentDetails.phoneNumber && (
                                            <div className="payment-detail-row">
                                                <div className="payment-detail-info">
                                                    <p>{t('paymentDetailPhone') || 'Номер телефона'}</p>
                                                    <b>{paymentDetails.phoneNumber}</b>
                                                </div>
                                                <button
                                                    className="copy-button"
                                                    onClick={() => copyToClipboard(paymentDetails.phoneNumber)}
                                                >
                                                    <FaCopy size={16} />
                                                </button>
                                            </div>
                                        )}
                                        {paymentDetails.owner && (
                                            <div className="payment-detail-row">
                                                <div className="payment-detail-info">
                                                    <p>{t('paymentDetailOwner') || 'Владелец'}</p>
                                                    <b>{paymentDetails.owner}</b>
                                                </div>
                                            </div>
                                        )}
                                        {paymentDetails.bankName && (
                                            <div className="payment-detail-row">
                                                <div className="payment-detail-info">
                                                    <p>{t('paymentDetailBank')}</p>
                                                    <b>{paymentDetails.bankName}</b>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {paymentDetails.requisites && (
                                    <>
                                        {/* Информация о криптовалюте и сети */}
                                        {paymentDetails.cryptoCurrency && (
                                            <div className="payment-detail-row">
                                                <div className="payment-detail-info">
                                                    <p>{t('paymentDetailCrypto', 'Криптовалюта')}</p>
                                                    <b>
                                                        {paymentDetails.cryptoCurrency === 'USDT-INTERNAL' ? 'USDT' : paymentDetails.cryptoCurrency}
                                                    </b>
                                                </div>
                                            </div>
                                        )}

                                        {/* Определяем сеть на основе криптовалюты */}
                                        <div className="payment-detail-row">
                                            <div className="payment-detail-info">
                                                <p>{t('paymentDetailNetwork', 'Сеть')}</p>
                                                <b>
                                                    {paymentDetails.cryptoCurrency === 'USDTTRC20' ? 'TRC20 (Tron)' :
                                                     paymentDetails.cryptoCurrency || 'Unknown'}
                                                </b>
                                            </div>
                                        </div>

                                        <div className="payment-detail-row">
                                            <div className="payment-detail-info crypto">
                                                <p>{t('paymentDetailWallet')}</p>
                                                <b>{paymentDetails.requisites}</b>
                                            </div>
                                            <button
                                                className="copy-button"
                                                onClick={() => copyToClipboard(paymentDetails.requisites)}
                                            >
                                                <FaCopy size={16} />
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* Убираем QR код для криптоплатежей */}
                                {paymentDetails.qrCode && (
                                    <div className="qr-code-container">
                                        <img
                                            src={paymentDetails.qrCode}
                                            alt="QR-код для оплаты"
                                            className="qr-code-image"
                                        />
                                    </div>
                                )}

                            </div>







                        </div>

                        {/* Универсальная кнопка оплаты для всех провайдеров */}
                        {paymentDetails.paymentPageUrl && (
                            <div className="payment-link-block" style={{ marginBottom: '20px' }}>
                                <a
                                    href={paymentDetails.paymentPageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="button-payment"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px',
                                        background: 
                                            (paymentDetails.provider?.includes('tpay'))
                                                ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                                                : (paymentDetails.provider?.includes('alfa') && paymentDetails.requisites)
                                                ? 'linear-gradient(135deg, #F7931A 0%, #FF8C00 100%)'
                                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: (paymentDetails.provider?.includes('tpay')) ? '#000' : '#fff',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '15px 30px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        textDecoration: 'none',
                                        boxShadow: 
                                            (paymentDetails.provider?.includes('tpay'))
                                                ? '0 4px 12px rgba(255, 215, 0, 0.3)'
                                                : (paymentDetails.provider?.includes('alfa') && paymentDetails.requisites)
                                                ? '0 4px 12px rgba(247, 147, 26, 0.3)'
                                                : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {paymentDetails.provider?.includes('tpay') && (
                                        <img src="/assets/methods/TPay.png" alt="TPay" width={24} height={24} />
                                    )}
                                    {(paymentDetails.provider?.includes('alfa') && paymentDetails.requisites) ? '₿ Оплатить криптой' : 'Оплатить'}
                                </a>
                            </div>
                        )}

                        {!isPaid ? (
                            <div className="payment-timer">
                                <div className="spinner"></div>
                                <span>{t('paymentDetailTimer', { time: formatTime(timeLeft) })}</span>
                            </div>
                        ) : (
                            <p className="payment-status-text">{t('paymentModal.errors.success.dop.pendingCheck')}</p>
                        )}

                        {!isPaid && (
                            <button className="button-payment" onClick={async () => {
                                const success = await updatePaymentStatus();
                                if (success) {
                                    setIsPaid(true);
                                }
                            }}>{t('buttonIPaid')}</button>
                        )}



                    </>
                )}

                {status === "success" && (
                    <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: 32, borderRadius: 20, background: 'linear-gradient(120deg, #571CA5 0%, #8443b9 100%)', color: '#fff', boxShadow: '0 4px 24px 0 rgba(87, 28, 165, 0.10)'
                    }}>
                        <div style={{ background: '#fff', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                            <svg width="36" height="36" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="18" r="18" fill="#4CAF50" /><path d="M11 19.5L16 24.5L25 15.5" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </div>
                        <h2 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 10px 0' }}>
                            {transactionType === "withdraw" ? t("paymentModal.errors.success.dop.successWithdrawTitle") : t("paymentModal.errors.success.dop.successDepositTitle")}
                        </h2>
                        <p style={{ fontSize: 16, margin: 0, marginBottom: 18 }}>
                            {transactionType === "withdraw"
                                ? t("paymentModal.errors.success.dop.successWithdrawMsg")
                                : t("paymentModal.errors.success.dop.successDepositMsg")}
                        </p>
                        <button onClick={handleClose} style={{ marginTop: 10, background: '#fff', color: '#571CA5', border: 'none', borderRadius: 10, padding: '12px 32px', fontWeight: 600, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 8px 0 #571CA522' }}>{t("paymentModal.errors.success.dop.close")}</button>
                    </div>
                )}

                {status === "denied" && (
                    <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: 32, borderRadius: 20, background: 'linear-gradient(120deg, #ff4444 0%, #b90000 100%)', color: '#fff', boxShadow: '0 4px 24px 0 rgba(255, 68, 68, 0.10)', margin: '40px 0'
                    }}>
                        <div style={{ background: '#fff', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                            <svg width="36" height="36" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="18" r="18" fill="#ff4444" /><path d="M12 12L24 24" stroke="#fff" strokeWidth="3" strokeLinecap="round" /><path d="M24 12L12 24" stroke="#fff" strokeWidth="3" strokeLinecap="round" /></svg>
                        </div>
                        <h2 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 10px 0' }}>{t("paymentModal.errors.success.dop.deniedTitle")}</h2>
                        <p style={{ fontSize: 16, margin: 0, marginBottom: 18 }}>{t("paymentModal.errors.success.dop.deniedMsg")}</p>
                        
                        {/* Комментарий администратора */}
                        {paymentDetails?.admin_comment && (
                            <div style={{ 
                                width: '100%', 
                                marginTop: 20, 
                                padding: 16, 
                                background: 'rgba(255, 255, 255, 0.15)', 
                                borderRadius: 12,
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}>
                                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, opacity: 0.9 }}>💬 Комментарий администратора:</div>
                                <div style={{ fontSize: 15, lineHeight: 1.5, fontWeight: 500 }}>
                                    {paymentDetails.admin_comment}
                                </div>
                            </div>
                        )}
                        
                        <button onClick={handleClose} style={{ marginTop: 20, background: '#fff', color: '#ff4444', border: 'none', borderRadius: 10, padding: '12px 32px', fontWeight: 600, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 8px 0 #ff444422' }}>{t("paymentModal.errors.success.dop.close")}</button>
                    </div>
                )}

                {status === "waiting_withdrawal" && (
                    <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: 32, borderRadius: 20, background: 'linear-gradient(120deg, #ffec80 0%, #ffb300 100%)', color: '#23232e', boxShadow: '0 4px 24px 0 rgba(255, 179, 0, 0.10)', margin: '40px 0'
                    }}>
                        <div style={{ background: '#fff', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                            <svg width="36" height="36" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="18" r="18" fill="#ffb300" /><path d="M18 10V20" stroke="#fff" strokeWidth="3" strokeLinecap="round" /><circle cx="18" cy="26" r="2" fill="#fff" /></svg>
                        </div>
                        <h2 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 10px 0' }}>
                            {t("paymentModal.withdrawalWaitingTitle", "Заявка на вывод ожидает обработки")}
                        </h2>
                        <p style={{ fontSize: 16, margin: 0, marginBottom: 18 }}>
                            {t("paymentModal.withdrawalWaitingMsg", "Ожидайте, заявка будет обработана оператором в ближайшее время.")}
                        </p>
                        <button onClick={handleClose} style={{ marginTop: 10, background: '#fff', color: '#ffb300', border: 'none', borderRadius: 10, padding: '12px 32px', fontWeight: 600, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 8px 0 #ffb30022' }}>{t("paymentModal.errors.success.dop.close")}</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentModal;
